import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { extendedMessagesApi } from "services/messages";
import { textRoutingApi } from "services/text-routing";
import { AssignedTag } from "services/types/assigned-tag.type";
import { Tag } from "services/types/tags.type";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

// "mine" / "my-clinics" / "unassigned" exist only while patient text routing is on
// (`useTextRoutingEnabled().routingOn`); the action bar resets them to "all" otherwise.
export type DialogsFilter =
  | "all"
  | "assigned"
  | "assigned-to-me"
  | "unread"
  | "mine"
  | "my-clinics"
  | "unassigned";

/**
 * A completed mark-as-read mutation, exposed to the dialog list so it can
 * update its locally accumulated pages directly — tag invalidation only
 * refetches the currently subscribed offset, so deep-scrolled rows would
 * otherwise stay stale until refresh (SN-18999). `patientId === null` means
 * all patients. `seq` makes consecutive events distinct.
 */
export type MessagesReadEvent = {
  seq: number;
  patientId: number | null;
};

/**
 * A conversation's tags changed (added/removed in the thread header). Same
 * accumulated-pages problem as MessagesReadEvent: the dialog list applies the
 * new tag set to its local rows directly instead of refetching. `tags` is the
 * full post-change set for that patient.
 */
export type ConversationTagsEvent = {
  seq: number;
  patientId: number | null;
  tags: AssignedTag[];
};

/**
 * Patient text routing: a conversation was marked Done (no reply needed). Same
 * accumulated-pages problem as MessagesReadEvent — the list clears the row's
 * waiting state locally (and drops it from a waiting-only list).
 */
export type InboxDoneEvent = {
  seq: number;
  patientId: number | null;
};

interface InboxState {
  includeMessagesFromOtherClinics: boolean;
  orderByMostRecentMessage: boolean;
  hideAutomatedTexts: boolean;
  dialogsFilter: DialogsFilter;
  messagesReadEvent: MessagesReadEvent;
  showOnlyUnread: boolean;
  conversationTagsFilter: Tag[];
  conversationTagsEvent: ConversationTagsEvent;
  // Patient text routing. `null` = the view's default (on for the routing views,
  // off for All conversations); reset whenever the tab changes.
  waitingOnlyOverride: boolean | null;
  longestWaitingFirst: boolean;
  // Unanswered texts (UNANSWERED_TEXTS_FEATURE): hide waiting conversations whose latest text
  // the AI marked "no reply needed". Persisted like the other list preferences; default on.
  hideAiNoReply: boolean;
  inboxDoneEvent: InboxDoneEvent;
  // Patient text routing: the Inbox opens on "Mine" once per page load for each
  // `${practiceId}:${userId}` session — a later choice of "All conversations"
  // sticks, but a different user or practice in the same tab gets the default.
  routingDefaultAppliedFor: string | null;
  // Needs a reply (ENG-2223): where the Inbox was (path + search) when the red tab was
  // clicked, so "Back to inbox" returns to the same tab and conversation. Session only.
  needsReplyReturnPath: string | null;
  // Texts not delivered (Intercom 215476575290017): the same, for the orange tab.
  textsNotDeliveredReturnPath: string | null;
}

const includeMessagesFromOtherClinicsStorage = setupLocalStorageAPI(
  "includeMessagesFromOtherClinics"
);

const orderByMostRecentMessageStorage = setupLocalStorageAPI(
  "orderByMostRecentMessage"
);

// Persisted per browser, exactly like includeMessagesFromOtherClinics: read state is a working
// preference, not a per-visit choice (ENG-2081).
const showOnlyUnreadStorage = setupLocalStorageAPI("showOnlyUnread");

// Persisted like the other inbox preferences so the front desk can leave
// their follow-up queue pinned across reloads.
const conversationTagsFilterStorage = setupLocalStorageAPI(
  "inboxConversationTagsFilter"
);

const hideAiNoReplyStorage = setupLocalStorageAPI("inboxHideAiNoReply");

const readStoredConversationTagsFilter = (): Tag[] => {
  const stored = conversationTagsFilterStorage.getData([]);
  return Array.isArray(stored)
    ? stored.filter((tag) => Boolean(tag && typeof tag.id === "number"))
    : [];
};

const initialState: InboxState = {
  includeMessagesFromOtherClinics:
    includeMessagesFromOtherClinicsStorage.getData(true),
  orderByMostRecentMessage: orderByMostRecentMessageStorage.getData(false),
  showOnlyUnread: showOnlyUnreadStorage.getData(false),
  hideAutomatedTexts: false,
  dialogsFilter: "all",
  messagesReadEvent: { seq: 0, patientId: null },
  conversationTagsFilter: readStoredConversationTagsFilter(),
  conversationTagsEvent: { seq: 0, patientId: null, tags: [] },
  waitingOnlyOverride: null,
  longestWaitingFirst: true,
  hideAiNoReply: hideAiNoReplyStorage.getData(true) !== false,
  inboxDoneEvent: { seq: 0, patientId: null },
  routingDefaultAppliedFor: null,
  needsReplyReturnPath: null,
  textsNotDeliveredReturnPath: null
};

const inboxSlice = createSlice({
  name: "inbox",
  initialState,
  reducers: {
    setIncludeMessagesFromOtherClinics: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.includeMessagesFromOtherClinics = action.payload;
    },
    toggleShowOnlyUnread: (state) => {
      state.showOnlyUnread = !state.showOnlyUnread;
      showOnlyUnreadStorage.updateData(state.showOnlyUnread);
    },
    toggleIncludeMessagesFromOtherClinics: (state) => {
      state.includeMessagesFromOtherClinics =
        !state.includeMessagesFromOtherClinics;
      includeMessagesFromOtherClinicsStorage.updateData(
        state.includeMessagesFromOtherClinics
      );
    },
    toggleOrderByMostRecentMessage: (state) => {
      state.orderByMostRecentMessage = !state.orderByMostRecentMessage;
      orderByMostRecentMessageStorage.updateData(
        state.orderByMostRecentMessage
      );
    },
    setHideAutomatedTexts: (state, action: PayloadAction<boolean>) => {
      state.hideAutomatedTexts = action.payload;
    },
    toggleHideAutomatedTexts: (state) => {
      state.hideAutomatedTexts = !state.hideAutomatedTexts;
    },
    setDialogsFilter: (state, action: PayloadAction<DialogsFilter>) => {
      if (state.dialogsFilter !== action.payload) {
        state.waitingOnlyOverride = null;
      }
      state.dialogsFilter = action.payload;
    },
    setWaitingOnlyOverride: (state, action: PayloadAction<boolean | null>) => {
      state.waitingOnlyOverride = action.payload;
    },
    setLongestWaitingFirst: (state, action: PayloadAction<boolean>) => {
      state.longestWaitingFirst = action.payload;
    },
    setHideAiNoReply: (state, action: PayloadAction<boolean>) => {
      state.hideAiNoReply = action.payload;
      hideAiNoReplyStorage.updateData(action.payload);
    },
    setNeedsReplyReturnPath: (state, action: PayloadAction<string | null>) => {
      state.needsReplyReturnPath = action.payload;
    },
    setTextsNotDeliveredReturnPath: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.textsNotDeliveredReturnPath = action.payload;
    },
    markRoutingDefaultApplied: (state, action: PayloadAction<string>) => {
      state.routingDefaultAppliedFor = action.payload;
    },
    setConversationTagsFilter: (state, action: PayloadAction<Tag[]>) => {
      state.conversationTagsFilter = action.payload;
      conversationTagsFilterStorage.updateData(action.payload);
    },
    publishConversationTagsEvent: (
      state,
      action: PayloadAction<{ patientId: number; tags: AssignedTag[] }>
    ) => {
      state.conversationTagsEvent = {
        seq: state.conversationTagsEvent.seq + 1,
        patientId: action.payload.patientId,
        tags: action.payload.tags
      };
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      extendedMessagesApi.endpoints.readAllPatientMessages.matchFulfilled,
      (state, action) => {
        state.messagesReadEvent = {
          seq: state.messagesReadEvent.seq + 1,
          patientId: action.meta.arg.originalArgs
        };
      }
    );
    builder.addMatcher(
      textRoutingApi.endpoints.markInboxDone.matchFulfilled,
      (state, action) => {
        state.inboxDoneEvent = {
          seq: state.inboxDoneEvent.seq + 1,
          patientId: action.meta.arg.originalArgs.patientId
        };
      }
    );
    builder.addMatcher(
      extendedMessagesApi.endpoints.readAllMessagesGlobal.matchFulfilled,
      (state) => {
        state.messagesReadEvent = {
          seq: state.messagesReadEvent.seq + 1,
          patientId: null
        };
      }
    );
  }
});

export const {
  setIncludeMessagesFromOtherClinics,
  toggleIncludeMessagesFromOtherClinics,
  toggleShowOnlyUnread,
  toggleOrderByMostRecentMessage,
  setHideAutomatedTexts,
  toggleHideAutomatedTexts,
  setDialogsFilter,
  setWaitingOnlyOverride,
  setLongestWaitingFirst,
  setHideAiNoReply,
  markRoutingDefaultApplied,
  setNeedsReplyReturnPath,
  setTextsNotDeliveredReturnPath,
  setConversationTagsFilter,
  publishConversationTagsEvent
} = inboxSlice.actions;

export const selectInboxState = (state: { inbox: InboxState }) => state.inbox;

export const getIncludeMessagesFromOtherClinicsState = (state: {
  inbox: InboxState;
}): boolean => state.inbox.includeMessagesFromOtherClinics;

export const getOrderByMostRecentMessageState = (state: {
  inbox: InboxState;
}) => state.inbox.orderByMostRecentMessage;

export const getShowOnlyUnreadState = (state: { inbox: InboxState }): boolean =>
  state.inbox.showOnlyUnread;

export const getHideAutomatedTextsState = (state: {
  inbox: InboxState;
}): boolean => state.inbox.hideAutomatedTexts;

export const getDialogsFilterState = (state: {
  inbox: InboxState;
}): DialogsFilter => state.inbox.dialogsFilter;

export const getWaitingOnlyOverrideState = (state: {
  inbox: InboxState;
}): boolean | null => state.inbox.waitingOnlyOverride;

export const getLongestWaitingFirstState = (state: {
  inbox: InboxState;
}): boolean => state.inbox.longestWaitingFirst;

export const getHideAiNoReplyState = (state: { inbox: InboxState }): boolean =>
  state.inbox.hideAiNoReply;

export const getRoutingDefaultAppliedForState = (state: {
  inbox: InboxState;
}): string | null => state.inbox.routingDefaultAppliedFor;

export const getNeedsReplyReturnPathState = (state: {
  inbox: InboxState;
}): string | null => state.inbox.needsReplyReturnPath;

export const getTextsNotDeliveredReturnPathState = (state: {
  inbox: InboxState;
}): string | null => state.inbox.textsNotDeliveredReturnPath;

export const getInboxDoneEventState = (state: {
  inbox: InboxState;
}): InboxDoneEvent => state.inbox.inboxDoneEvent;

export const getMessagesReadEventState = (state: {
  inbox: InboxState;
}): MessagesReadEvent => state.inbox.messagesReadEvent;

export const getConversationTagsFilterState = (state: {
  inbox: InboxState;
}): Tag[] => state.inbox.conversationTagsFilter;

export const getConversationTagsEventState = (state: {
  inbox: InboxState;
}): ConversationTagsEvent => state.inbox.conversationTagsEvent;

export default inboxSlice.reducer;
//
